import { withAlpha } from "@/lib/color";
import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
    trFoot, trStyle, trInjury, trRegion, trForm,
    trWeakFootUsage, trWeakFootAccuracy, trPositionFull, trPositionShort,
    trStat, trSkill,
} from "@/lib/pesTranslations";
import PlayerAvatar from "@/components/PlayerAvatar";
import { FavoriteStar } from "@/components/FavoriteStar";
import { useFavorites } from "@/context/FavoritesContext";
import { usePersistedState } from "@/lib/usePersistedState";

const POSITION_COLORS = {
    GK: "rgb(var(--tc-e8ff00))",
    CB: "rgb(var(--tc-00e5ff))", LB: "rgb(var(--tc-00e5ff))", RB: "rgb(var(--tc-00e5ff))",
    DMF: "rgb(var(--tc-2bb6ff))", CMF: "rgb(var(--tc-2bb6ff))", LMF: "rgb(var(--tc-2bb6ff))", RMF: "rgb(var(--tc-2bb6ff))", AMF: "rgb(var(--tc-2bb6ff))",
    LWF: "rgb(var(--tc-ff6b00))", RWF: "rgb(var(--tc-ff6b00))", SS: "rgb(var(--tc-ff6b00))", CF: "rgb(var(--tc-ff6b00))",
};

const PAGE_SIZE = 24;

// Parsing/serializzazione dei filtri statistica multipli.
// Formato URL: "Speed:85-99,Dribbling:80-,Tackling:-70"
function parseStatFilters(str) {
    if (!str) return [];
    return str
        .split(",")
        .map((part) => {
            const ci = part.indexOf(":");
            const stat = (ci >= 0 ? part.slice(0, ci) : part).trim();
            const rng = ci >= 0 ? part.slice(ci + 1) : "";
            const di = rng.indexOf("-");
            const min = (di >= 0 ? rng.slice(0, di) : rng).trim();
            const max = (di >= 0 ? rng.slice(di + 1) : "").trim();
            return { stat, min, max };
        })
        .filter((r) => r.stat);
}

function serializeStatFilters(rows) {
    return rows
        .filter((r) => r.stat)
        .map((r) => `${r.stat}:${r.min || ""}-${r.max || ""}`)
        .join(",");
}

export default function Players() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [page, setPage] = usePersistedState("players.page", 1);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [statRows, setStatRows] = useState(() => parseStatFilters(params.get("stat_filters")));
    const { favorites } = useFavorites();
    const debounceRef = useRef(null);

    const get = (k, def = "") => params.get(k) || def;

    const fetchPlayers = useCallback(async () => {
        setLoading(true);
        try {
            const p = { page, page_size: PAGE_SIZE };
            params.forEach((value, key) => {
                if (key !== "page" && value) p[key] = value;
            });
            if (!p.sort) p.sort = "overall";
            if (!p.order) p.order = "desc";
            const { data } = await api.get("/players", { params: p });
            setItems(data.items);
            setTotal(data.total);
        } finally {
            setLoading(false);
        }
    }, [page, params]);

    /* eslint-disable */
    useEffect(() => {
        fetchPlayers();
    }, [fetchPlayers]);

    useEffect(() => {
        api.get("/players/filters").then((r) => setFilters(r.data)).catch(() => {});
    }, []);
    /* eslint-enable */

    // Se "solo preferiti" è attivo, ricarica quando cambiano i preferiti.
    useEffect(() => {
        if (params.get("favorites_only") === "1") fetchPlayers();
        // eslint-disable-next-line
    }, [favorites]);

    // Riallinea la pagina se supera il totale (es. con filtri/preferiti che riducono i risultati).
    useEffect(() => {
        const tp = Math.max(1, Math.ceil(total / PAGE_SIZE));
        if (page > tp) setPage(tp);
        // eslint-disable-next-line
    }, [total]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, value);
        next.delete("page");
        setPage(1);
        setParams(next, { replace: true });
    };

    const onSearchChange = (v) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => updateParam("q", v), 300);
    };

    const clearAll = () => {
        setParams({}, { replace: true });
        setStatRows([]);
        setPage(1);
    };

    // Gestione filtri statistica multipli (aggiungi/rimuovi righe)
    const applyStatRows = (rows) => {
        setStatRows(rows);
        updateParam("stat_filters", serializeStatFilters(rows));
    };
    const addStatRow = () => setStatRows([...statRows, { stat: "", min: "", max: "" }]);
    const updateStatRow = (i, patch) =>
        applyStatRows(statRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const removeStatRow = (i) => applyStatRows(statRows.filter((_, idx) => idx !== i));

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const activeFilterCount = Array.from(params.keys()).filter((k) => k !== "page").length;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="players-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="op-label text-t2bb6ff">// PLAYER DATABASE · AUTHENTIC</div>
                    {get("favorites_only") === "1" ? (
                        <>
                            <h1 className="op-heading text-5xl sm:text-6xl mt-3 mb-2">
                                I MIEI <span className="text-te8ff00">★ PREFERITI</span>
                            </h1>
                            <p className="text-t8f95a3 max-w-2xl text-sm leading-relaxed">
                                I giocatori che hai salvato come preferiti. Togli il filtro per tornare a tutto il database.
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="op-heading text-5xl sm:text-6xl mt-3 mb-2">
                                I <span className="op-gradient-text">GIOCATORI</span>
                            </h1>
                            <p className="text-t8f95a3 max-w-2xl text-sm leading-relaxed">
                                Database eFootball 2026 — versione <strong className="text-tffffff">Authentic</strong>
                                — importato da pesdb.net. Cerca, filtra, ordina.
                            </p>
                        </>
                    )}
                </div>
                {activeFilterCount > 0 && (
                    <button onClick={clearAll} className="op-btn op-btn-ghost" data-testid="clear-filters-btn">
                        Pulisci filtri ({activeFilterCount})
                    </button>
                )}
            </div>

            {/* Filters card */}
            <div className="op-card mt-8" data-testid="players-filters">
                <div className="p-4 flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        defaultValue={get("q")}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Cerca giocatore…"
                        className="op-input flex-1 min-w-[220px]"
                        data-testid="players-search-input"
                    />
                    <button
                        type="button"
                        onClick={() => updateParam("favorites_only", get("favorites_only") === "1" ? "" : "1")}
                        className={`op-btn flex items-center gap-2 ${get("favorites_only") === "1" ? "border-te8ff00 text-te8ff00" : "op-btn-ghost"}`}
                        title="Mostra solo i tuoi preferiti"
                        data-testid="players-favorites-toggle"
                    >
                        <span style={{ fontSize: "14px" }}>★</span> Solo preferiti
                    </button>
                    <SelectFilter
                        value={get("position")}
                        onChange={(v) => updateParam("position", v)}
                        placeholder="Ruolo"
                        options={(filters.positions || []).filter((v) => v && !v.includes(" "))}
                        translate={(v) => `${trPositionShort(v)} · ${trPositionFull(v)}`}
                        testId="players-position-select"
                    />
                    <SelectFilter
                        value={get("playing_style")}
                        onChange={(v) => updateParam("playing_style", v)}
                        placeholder="Stile di gioco"
                        options={filters.playing_styles}
                        translate={trStyle}
                        testId="players-playing-style-select"
                        wide
                    />
                    <SelectFilter
                        value={get("foot")}
                        onChange={(v) => updateParam("foot", v)}
                        placeholder="Piede"
                        options={filters.foots}
                        translate={trFoot}
                        testId="players-foot-select"
                    />
                    <select
                        value={`${get("sort", "overall")}:${get("order", "desc")}`}
                        onChange={(e) => {
                            const [s, o] = e.target.value.split(":");
                            const next = new URLSearchParams(params);
                            next.set("sort", s);
                            next.set("order", o);
                            next.delete("page");
                            setPage(1);
                            setParams(next, { replace: true });
                        }}
                        className="op-input"
                        data-testid="players-sort-select"
                    >
                        <option value="market_value:desc">Valore TM ↓</option>
                        <option value="market_value:asc">Valore TM ↑</option>
                        <option value="overall:desc">VG ↓</option>
                        <option value="overall:asc">VG ↑</option>
                        <option value="purchase_wage:desc">Stipendio ↓</option>
                        <option value="purchase_wage:asc">Stipendio ↑</option>
                        <option value="name:asc">Nome A-Z</option>
                        <option value="age:asc">Età ↑</option>
                        <option value="age:desc">Età ↓</option>
                        <option value="height:desc">Altezza ↓</option>
                    </select>
                </div>

                {/* Availability toggle */}
                <div className="border-t border-t22252a px-4 py-3 flex flex-wrap gap-2 items-center" data-testid="availability-toggle">
                    <span className="op-mono text-[10px] text-t8f95a3 uppercase mr-1">Disponibilità:</span>
                    {[
                        { v: "", label: "Tutti" },
                        { v: "free", label: "Free Transfer" },
                        { v: "rostered", label: "In rosa" },
                    ].map((opt) => {
                        const active = (get("availability") || "") === opt.v;
                        return (
                            <button
                                key={opt.v || "all"}
                                onClick={() => updateParam("availability", opt.v)}
                                className="op-mono text-[11px] px-3 py-1.5 border transition-colors"
                                style={{
                                    background: active ? "rgb(var(--tc-2bb6ff))" : "transparent",
                                    color: active ? "rgb(var(--tc-000000))" : "rgb(var(--tc-2bb6ff))",
                                    borderColor: withAlpha("rgb(var(--tc-2bb6ff))", "88"),
                                }}
                                data-testid={`availability-${opt.v || "all"}`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {/* Quick range filters */}
                <div className="border-t border-t22252a p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <RangeFilter
                        label="VG"
                        minVal={get("overall_min")}
                        maxVal={get("overall_max")}
                        onMin={(v) => updateParam("overall_min", v)}
                        onMax={(v) => updateParam("overall_max", v)}
                        testId="overall"
                    />
                    <RangeFilter
                        label="Età"
                        minVal={get("age_min")}
                        maxVal={get("age_max")}
                        onMin={(v) => updateParam("age_min", v)}
                        onMax={(v) => updateParam("age_max", v)}
                        testId="age"
                    />
                    <RangeFilter
                        label="Altezza (cm)"
                        minVal={get("height_min")}
                        maxVal={get("height_max")}
                        onMin={(v) => updateParam("height_min", v)}
                        onMax={(v) => updateParam("height_max", v)}
                        testId="height"
                    />
                    <RangeFilter
                        label="Peso (kg)"
                        minVal={get("weight_min")}
                        maxVal={get("weight_max")}
                        onMin={(v) => updateParam("weight_min", v)}
                        onMax={(v) => updateParam("weight_max", v)}
                        testId="weight"
                    />
                    <RangeFilter
                        label="Stipendio (mln)"
                        minVal={get("wage_min")}
                        maxVal={get("wage_max")}
                        onMin={(v) => updateParam("wage_min", v)}
                        onMax={(v) => updateParam("wage_max", v)}
                        testId="wage"
                    />
                </div>

                {/* Advanced toggle */}
                <div className="border-t border-t22252a">
                    <button
                        onClick={() => setAdvancedOpen(!advancedOpen)}
                        className="op-label w-full text-left px-4 py-3 hover:text-tffffff transition-colors"
                        data-testid="advanced-toggle"
                    >
                        {advancedOpen ? "▾" : "▸"} Filtri avanzati (nazione, infortuni, piede debole…)
                    </button>
                    {advancedOpen && (
                        <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <SelectFilter
                                value={get("nationality")}
                                onChange={(v) => updateParam("nationality", v)}
                                placeholder="Nazione"
                                options={filters.nationalities}
                                testId="players-nationality-select"
                                wide
                            />
                            <SelectFilter
                                value={get("injury_resistance")}
                                onChange={(v) => updateParam("injury_resistance", v)}
                                placeholder="Resistenza infortuni"
                                options={filters.injury_resistance}
                                translate={trInjury}
                                testId="injury-resistance-select"
                                wide
                            />
                            <SelectFilter
                                value={get("region")}
                                onChange={(v) => updateParam("region", v)}
                                placeholder="Region"
                                options={filters.regions}
                                translate={trRegion}
                                testId="region-select"
                                wide
                            />
                            <SelectFilter
                                value={get("form")}
                                onChange={(v) => updateParam("form", v)}
                                placeholder="Forma"
                                options={filters.form}
                                translate={trForm}
                                testId="form-select"
                                wide
                            />
                            <SelectFilter
                                value={get("weak_foot_usage")}
                                onChange={(v) => updateParam("weak_foot_usage", v)}
                                placeholder="Uso piede debole"
                                options={filters.weak_foot_usage}
                                translate={trWeakFootUsage}
                                testId="weak-foot-usage-select"
                                wide
                            />
                            <SelectFilter
                                value={get("weak_foot_accuracy")}
                                onChange={(v) => updateParam("weak_foot_accuracy", v)}
                                placeholder="Precisione piede debole"
                                options={filters.weak_foot_accuracy}
                                translate={trWeakFootAccuracy}
                                testId="weak-foot-accuracy-select"
                                wide
                            />
                            <SelectFilter
                                value={get("skill")}
                                onChange={(v) => updateParam("skill", v)}
                                placeholder="Abilità (es. Colpo di tacco)"
                                options={filters.skills}
                                translate={trSkill}
                                testId="players-skill-select"
                                wide
                            />
                            {/* Filtri multipli per statistica: aggiungi quante voci vuoi */}
                            <div className="sm:col-span-2 lg:col-span-3 border border-t22252a p-3" data-testid="players-stat-filter">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="op-label text-[10px]">Filtra per statistiche</div>
                                    <button
                                        type="button"
                                        onClick={addStatRow}
                                        className="op-mono text-[11px] text-t2bb6ff hover:underline"
                                        data-testid="players-stat-add"
                                    >
                                        + Aggiungi statistica
                                    </button>
                                </div>
                                {statRows.length === 0 ? (
                                    <div className="op-mono text-[11px] text-t5c6370">
                                        Nessuna statistica. Clicca "+ Aggiungi statistica" per filtrare (Velocità, Dribbling, Contrasto…). Puoi aggiungerne quante vuoi.
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {statRows.map((row, i) => (
                                            <div
                                                key={i}
                                                className="grid grid-cols-[minmax(0,1fr)_70px_70px_auto] gap-2 items-center"
                                                data-testid={`players-stat-row-${i}`}
                                            >
                                                <select
                                                    value={row.stat}
                                                    onChange={(e) => updateStatRow(i, { stat: e.target.value })}
                                                    className="op-input"
                                                    data-testid={`players-stat-select-${i}`}
                                                >
                                                    <option value="">Scegli statistica…</option>
                                                    {(filters.stats || []).map((s) => (
                                                        <option key={s} value={s}>{trStat(s)}</option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="number"
                                                    value={row.min}
                                                    onChange={(e) => updateStatRow(i, { min: e.target.value })}
                                                    placeholder="min"
                                                    disabled={!row.stat}
                                                    className="op-input op-mono py-1.5 text-sm disabled:opacity-40"
                                                    data-testid={`players-stat-min-${i}`}
                                                />
                                                <input
                                                    type="number"
                                                    value={row.max}
                                                    onChange={(e) => updateStatRow(i, { max: e.target.value })}
                                                    placeholder="max"
                                                    disabled={!row.stat}
                                                    className="op-input op-mono py-1.5 text-sm disabled:opacity-40"
                                                    data-testid={`players-stat-max-${i}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeStatRow(i)}
                                                    className="op-mono text-tff6b6b hover:text-tff3b30 px-2 text-sm"
                                                    title="Rimuovi questa statistica"
                                                    data-testid={`players-stat-remove-${i}`}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-end justify-between mt-6 mb-4 flex-wrap gap-2">
                <div className="op-mono text-sm text-t8f95a3" data-testid="players-total-count">
                    {total.toLocaleString("it-IT")} giocatori trovati
                </div>
                <div className="op-mono text-xs text-t5c6370">
                    Pagina {page} / {totalPages}
                </div>
            </div>

            {loading ? (
                <div className="op-label text-center py-12">Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="op-card p-12 text-center" data-testid="players-empty">
                    <div className="op-label">Nessun giocatore</div>
                    <p className="text-t8f95a3 text-sm mt-2">
                        {activeFilterCount > 0
                            ? "Nessun risultato con questi filtri. Prova a rimuoverne qualcuno."
                            : "Il DB è ancora vuoto. Un admin deve lanciare il sync da Admin → PES DB Sync."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="players-grid">
                    {items.map((p) => (
                        <PlayerCard key={p.pes_id} player={p} />
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="players-pagination">
                    <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="op-btn disabled:opacity-30" data-testid="players-prev-btn">← Precedente</button>
                    <span className="op-mono text-sm text-t8f95a3 px-3">{page} / {totalPages}</span>
                    <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="op-btn disabled:opacity-30" data-testid="players-next-btn">Successiva →</button>
                </div>
            )}
        </div>
    );
}

function SelectFilter({ value, onChange, placeholder, options, testId, wide, translate }) {
    const trans = translate || ((v) => v);
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`op-input ${wide ? "min-w-[180px]" : "min-w-[130px]"}`}
            data-testid={testId}
        >
            <option value="">{placeholder}</option>
            {(options || []).map((o) => (
                <option key={o} value={o}>{trans(o)}</option>
            ))}
        </select>
    );
}

function RangeFilter({ label, minVal, maxVal, onMin, onMax, testId }) {
    return (
        <div>
            <div className="op-label text-[10px] mb-2">{label}</div>
            <div className="flex items-center gap-2">
                <input
                    type="number"
                    value={minVal || ""}
                    onChange={(e) => onMin(e.target.value)}
                    placeholder="min"
                    className="op-input op-mono py-1.5 text-sm w-full"
                    data-testid={`${testId}-min`}
                />
                <span className="op-label text-t5c6370">→</span>
                <input
                    type="number"
                    value={maxVal || ""}
                    onChange={(e) => onMax(e.target.value)}
                    placeholder="max"
                    className="op-input op-mono py-1.5 text-sm w-full"
                    data-testid={`${testId}-max`}
                />
            </div>
        </div>
    );
}

function PlayerCard({ player }) {
    const posColor = POSITION_COLORS[player.position] || "rgb(var(--tc-2bb6ff))";
    const op = player.op_team;
    return (
        <Link
            to={`/giocatori/${player.pes_id}`}
            className="op-card group p-4 flex flex-col gap-2.5 hover:border-tffffff transition-colors duration-150 relative overflow-hidden"
            data-testid={`player-card-${player.pes_id}`}
        >
            <div className="absolute top-1.5 left-1.5 z-20">
                <FavoriteStar
                    pesId={player.pes_id}
                    size={18}
                    className="p-1 rounded-full bg-black/60 backdrop-blur-sm ring-1 ring-tffffff/15 hover:ring-te8ff00/50"
                />
            </div>
            {/* Background watermark of OP team logo (subtle) */}
            {op?.logo_url && (
                <img
                    src={op.logo_url}
                    alt=""
                    aria-hidden
                    className="pointer-events-none absolute right-[-12px] bottom-[-18px] w-32 h-32 object-contain opacity-[0.06] group-hover:opacity-[0.12] transition-opacity duration-200"
                    loading="lazy"
                />
            )}
            {/* Riga 1: foto + identità (ruolo/nazione/età + nome) + VG */}
            <div className="flex items-center gap-3 relative z-10">
                <PlayerAvatar
                    tmImage={op?.tm_image}
                    pesImage={player.image_url}
                    name={player.name}
                    size={48}
                    testId={`player-avatar-${player.pes_id}`}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="op-label text-[10px] px-1.5 py-0.5 border" style={{ color: posColor, borderColor: withAlpha(posColor, "55") }}>
                            {trPositionShort(player.position) || "?"}
                        </span>
                        <span className="op-mono text-xs text-t8f95a3 truncate">{player.nationality}</span>
                        {player.age != null && (
                            <span className="op-mono text-xs text-t8f95a3 shrink-0" data-testid={`player-age-${player.pes_id}`}>
                                · {player.age} anni
                            </span>
                        )}
                    </div>
                    <div className="text-tffffff font-semibold leading-tight break-words group-hover:text-t2bb6ff transition-colors">{player.name}</div>
                </div>
                <div className="text-center shrink-0 w-10">
                    <div className="op-heading text-2xl text-tffffff leading-none">{player.overall ?? "—"}</div>
                    <div className="op-label text-[9px] mt-0.5">VG</div>
                </div>
            </div>
            {/* Riga 2: squadra OP / svincolato + ingaggio */}
            <div className="flex items-center justify-between gap-2 relative z-10 border-t border-t22252a pt-2">
                {op ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                        {op.logo_url && (
                            <img src={op.logo_url} alt="" aria-hidden className="w-4 h-4 object-contain bg-tffffff rounded-sm p-px shrink-0" loading="lazy" />
                        )}
                        <span className="op-mono text-[11px] text-tffffff truncate" data-testid={`player-op-team-${player.pes_id}`}>
                            {op.name}
                        </span>
                        {op.is_duplicate && (
                            <span className="op-mono text-[9px] uppercase px-1 py-0 border border-te8ff00/60 text-te8ff00 shrink-0" title="Carta duplicata: stesso giocatore ma carta diversa in PES DB">D</span>
                        )}
                        {op.is_ugly && !op.is_duplicate && (
                            <span className="op-mono text-[9px] uppercase px-1 py-0 border border-tff6b00/60 text-tff6b00 shrink-0">U</span>
                        )}
                        {op.is_loaned_out && !op.is_duplicate && (
                            <span className="op-mono text-[9px] uppercase px-1 py-0 border border-t8f95a3/60 text-t8f95a3 shrink-0">L</span>
                        )}
                    </div>
                ) : (
                    <div className="op-mono text-[11px] text-t8f95a3 truncate" data-testid={`player-op-team-${player.pes_id}`}>
                        FREE TRANSFER
                    </div>
                )}
                {player.computed_wage != null && (
                    <div className="op-mono text-[11px] text-te8ff00 whitespace-nowrap shrink-0" data-testid={`player-wage-${player.pes_id}`}>
                        {formatWage(player.computed_wage)}
                    </div>
                )}
            </div>
        </Link>
    );
}

function formatWage(v) {
    if (v == null) return "—";
    if (v === 0) return "0";
    const n = v / 1_000_000;
    if (n >= 10) return `${n.toFixed(1)}mln`;
    return `${n.toFixed(2)}mln`;
}
